/**
 * allocation-schema.ts — ปันส่วนค่าใช้จ่ายส่วนกลาง (CONTEXT §5.4/§7, T068)
 *
 * กติกา:
 * - method: direct/percentage/revenue/orders/units
 * - percentage/direct: สัดส่วนต้องรวม = 100
 * - revenue/orders/units: ปันตาม driver (สัดส่วน driver_i / Σdriver)
 * - versioned + reproducible: ผลปันส่วน (allocation_results) immutable ผูก rule_version
 * - เงินคำนวณ Decimal; งวดสุดท้ายซับ rounding ให้ Σ = ยอดตั้งต้นพอดี
 */
import { z } from 'zod';
import { allocationMethod } from './entry-schema.js';
import { toDecimal, quantizeMoney, moneyToString, type Decimal } from './money.js';

export const allocationTargetInput = z.object({
  targetType: z.enum(['business', 'shop', 'channel', 'brand', 'sku', 'campaign']),
  targetId: z.string().trim().max(200).optional(),
  /** สำหรับ percentage/direct (0–100) */
  percentage: z.coerce.number().min(0).max(100).optional(),
  /** สำหรับ revenue/orders/units (ค่า driver ของ target นี้) */
  driver: z.coerce.number().min(0).optional(),
});
export type AllocationTargetInput = z.infer<typeof allocationTargetInput>;

export const allocationRuleInput = z.object({
  name: z.string().trim().min(1).max(200),
  method: allocationMethod,
  basis: z.string().trim().max(200).optional(),
  targets: z.array(allocationTargetInput).min(1).max(200),
});
export type AllocationRuleInput = z.infer<typeof allocationRuleInput>;

export const allocateInput = z.object({
  ruleId: z.string().uuid(),
  amount: z
    .string()
    .trim()
    .min(1)
    .refine((v) => {
      try {
        toDecimal(v);
        return true;
      } catch {
        return false;
      }
    }, 'จำนวนเงินไม่ถูกต้อง'),
  sourceEntryId: z.string().uuid().optional(),
});
export type AllocateInput = z.infer<typeof allocateInput>;

export interface AllocationSlice {
  targetType: string;
  targetId: string | null;
  amount: string;
}

const PERCENTAGE_METHODS = new Set(['percentage', 'direct']);

/** ตรวจว่าสัดส่วน percentage รวม = 100 (percentage/direct) */
export function assertPercentagesSum100(
  targets: { percentage?: number | undefined }[],
): { ok: boolean; total: number } {
  const total = targets.reduce((acc, t) => acc.plus(toDecimal(t.percentage ?? 0)), toDecimal('0'));
  return { ok: total.equals(100), total: Number(total.toString()) };
}

/**
 * คำนวณการปันส่วนยอด `amount` ไปยัง targets ตาม method
 * - percentage/direct: weight = percentage/100 (ต้องรวม 100)
 * - revenue/orders/units: weight = driver/Σdriver (Σdriver > 0)
 * งวดสุดท้ายซับ rounding ให้ Σ allocated = amount พอดี (money integrity)
 * throw Error ถ้าสัดส่วน/ไดรเวอร์ไม่ถูกต้อง (service แปลงเป็น 400)
 */
export function computeAllocation(params: {
  method: string;
  amount: string;
  targets: AllocationTargetInput[];
}): AllocationSlice[] {
  const { method, targets } = params;
  const amount = toDecimal(params.amount);
  if (targets.length === 0) throw new Error('ต้องมีปลายทางการปันส่วนอย่างน้อย 1 รายการ');

  let weights: Decimal[];
  if (PERCENTAGE_METHODS.has(method)) {
    const total = targets.reduce((a, t) => a.plus(toDecimal(t.percentage ?? 0)), toDecimal('0'));
    if (!total.equals(100)) {
      throw new Error(`สัดส่วนการปันส่วนรวม = ${total.toString()} ต้องเท่ากับ 100`);
    }
    weights = targets.map((t) => toDecimal(t.percentage ?? 0).div(100));
  } else {
    // revenue/orders/units → driver
    const total = targets.reduce((a, t) => a.plus(toDecimal(t.driver ?? 0)), toDecimal('0'));
    if (total.lte(0)) {
      throw new Error('ผลรวมของ driver (revenue/orders/units) ต้องมากกว่า 0');
    }
    weights = targets.map((t) => toDecimal(t.driver ?? 0).div(total));
  }

  const slices: AllocationSlice[] = [];
  let allocated = toDecimal('0');
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i]!;
    const isLast = i === targets.length - 1;
    const raw = isLast ? amount.minus(allocated) : quantizeMoney(amount.times(weights[i]!));
    allocated = allocated.plus(raw);
    slices.push({
      targetType: t.targetType,
      targetId: t.targetId ?? null,
      amount: moneyToString(raw),
    });
  }
  return slices;
}
